import { useEffect, useMemo, useState } from "react";
import { Constants, MeetingProvider } from "../../config/sdkConfig";
import { MeetingAppProvider } from "../../MeetingAppContextDef";
import { LeaveScreen } from "../screens/LeaveScreen";
import { JoiningScreenNew } from "../screens/JoiningScreenNew";
import { MeetingContainerNew } from "../../meeting/MeetingContainerNew";
import { useSiteContext } from "../../context/SiteContext";
import { LeaveScreenNew } from "../screens/LeaveScreenNew";
import { WebinarContainer } from "../../meeting/WebinarContainer";
import WebinerBrandingPre from "../webiner-branding/WebinerBrandingPre";
import WebinerBrandingPost from "../webiner-branding/WebinerBrandingPost";
import { useNavigate } from "react-router-dom";
import * as meetingServices from "../../services/meetingServices";
import { AgoraRTMProvider } from "../../context/AgoraRTMContext";
import { AgoraChatProvider } from "../../context/AgoraChatContext";
import { MeetingStateProvider } from "../../context/MeetingStateContext";
import ErrorBoundary from "../common/ErrorBoundary";
import RLinkLogo from "../common/RLinkLogo";
import { useWhiteLabel } from "../../context/WhiteLabelContext";
import { unlockIOSAudio } from "../../utils/iosAudioUnlock";
function RedirectToRegistration({ roomId }) {
  const navigate = useNavigate();
  useEffect(() => {
    if (!roomId) return;
    // Preserve the registration token so the event page can identify the
    // attendee and show a "You're registered — countdown" view instead of
    // the registration form.
    const params = new URLSearchParams(window.location.search);
    const token = params.get('r');
    const suffix = token ? `?r=${encodeURIComponent(token)}` : '';
    navigate(`/webinar/${roomId}/event${suffix}`, { replace: true });
  }, [roomId, navigate]);
  return null;
}

// Grid-recorder layouts — must match cropGridLayouts.js CROP_POSITIONS on
// the worker droplet (same order, same names, same x/y offsets).
const RECORDER_GRID_LAYOUTS = ['gallery', 'focus', 'speaker-top', 'speaker-side'];

function Conference({ forceGrid = false }) {
  const navigate = useNavigate();
  const { branding: wlBranding } = useWhiteLabel();
  const { setToken: setTokenContext, setIsAuthenticated, setUser, user, meetingRole, meetingSettings,
    webinarMode, setMeetingRole, setMeetingSettings, isAuthenticated,
    isMeetingReconnecting, customMeetingName
  } = useSiteContext();

  const [token, setToken] = useState("");
  const [meetingId, setMeetingId] = useState("");
  const [participantName, setParticipantName] = useState("");
  const [micOn, setMicOn] = useState(false);
  const [webcamOn, setWebcamOn] = useState(false);
  const [customAudioStream, setCustomAudioStream] = useState(null);
  const [customVideoStream, setCustomVideoStream] = useState(null);
  const [isMeetingStarted, setMeetingStarted] = useState(false);
  const [isMeetingLeft, setIsMeetingLeft] = useState(false);
  const [show, setShow] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [joiningToken, setJoiningToken] = useState(null);

  // Store stable participantId to prevent meeting reinitialization
  // Generate once and store in state to avoid Math.random() on every render
  const [stableParticipantId, setStableParticipantId] = useState(null);

  // Store initial meeting role to prevent meeting reinitialization when role changes
  // (e.g., when promoted to co-host). The initial role is used in MeetingProvider config.
  // Dynamic role changes are tracked via coHosts set in SiteContext.
  const [initialMeetingRole, setInitialMeetingRole] = useState(null);

  // Detect if joining from a breakout room redirect
  const [isFromBreakout] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('fromBreakout') === '1';
  });

  const isMobile = window.matchMedia(
    "only screen and (max-width: 768px)"
  ).matches;

  const checkValidMeeting = async (roomName, isauth, retryAsGuest = false) => {
    try {
      console.log("New version 2")
      // Pass registration email so backend can assign co-host/presenter roles for registrants
      const webinarReg = localStorage.getItem(`webinar_reg_${roomName}`);
      const regEmail = webinarReg ? JSON.parse(webinarReg)?.email : null;
      const response = await meetingServices.getMeetingByName(roomName, regEmail);
      const meeting = response.meeting;
      // console.log("meeting", meeting);
      if (meeting) {
        // console.log("meeting", meeting);
        if (meeting.scheduledStartTime) {
          meeting.scheduledStartTime = meeting.scheduledStartTime.replace(".000Z", "");
        }
        if (meeting.scheduledEndTime) {
          meeting.scheduledEndTime = meeting.scheduledEndTime.replace(".000Z", "");
        }
        // console.log("meeting123", meeting);
        setMeetingSettings(meeting);

        // For breakout redirects: restore the participant's role from the previous room.
        // Co-host promotion is via PubSub (in-memory) and doesn't persist to the DB,
        // so getRole() on temp breakout meetings returns "participant" for co-hosts.
        // The saved role ensures co-hosts keep their role in breakout rooms and when returning.
        let effectiveRole = meeting.role;
        if (isFromBreakout) {
          const savedRole = localStorage.getItem('rlink_breakout_role');
          if (savedRole && ["co-host", "speaker", "presenter"].includes(savedRole)) {
            effectiveRole = savedRole;
          }
          localStorage.removeItem('rlink_breakout_role');
        }
        setMeetingRole(effectiveRole);
        setMeetingId(meeting.roomId);

        // Capture initial role for MeetingProvider config
        // This prevents reinitialization when role changes dynamically (e.g., co-host promotion)
        if (!initialMeetingRole) {
          setInitialMeetingRole(effectiveRole);
        }

        // Set initial mic/webcam state based on role:
        // - Hosts/moderators/co-hosts: enable mic and webcam by default
        // - Participants: disable mic and webcam by default (they can enable if allowed)
        // - Breakout redirect: keep whatever state they had in the previous room
        const isRecorderBot = new URLSearchParams(window.location.search).get('recorder') === 'true';
        const isHostRole = ["moderator", "co-host", "host"].includes(meeting.role);

        // Participant cap: force mic/webcam OFF for view-only joiners.
        // They joined after the 100-participant cap was hit — they can
        // watch and listen but cannot publish audio or video.
        if (meeting.viewOnly) {
          setMicOn(false);
          setWebcamOn(false);
        } else if (isFromBreakout) {
          // Coming from breakout redirect — restore mic/webcam from previous room state
          const savedMic = localStorage.getItem('rlink_meeting_micOn');
          const savedCam = localStorage.getItem('rlink_meeting_webcamOn');
          setMicOn(savedMic === '1');
          setWebcamOn(savedCam === '1');
          localStorage.removeItem('rlink_meeting_micOn');
          localStorage.removeItem('rlink_meeting_webcamOn');
        } else if (isHostRole) {
          // Respect the user's "Mute my microphone" / "Turn off my camera"
          // join preferences saved in Settings. Without this, hosts always
          // joined unmuted regardless of the toggle (the JoiningScreen
          // preview honours these flags but only by skipping its own
          // setMicOn(true) — it cannot undo a parent that already set true).
          let muteOnJoin = false;
          let cameraOffOnJoin = false;
          try {
            const saved = localStorage.getItem('rlink_settings');
            if (saved) {
              const settings = JSON.parse(saved);
              muteOnJoin = settings.muteOnJoin === true;
              cameraOffOnJoin = settings.cameraOffOnJoin === true;
            }
          } catch (_) { /* fall back to defaults */ }
          setMicOn(!muteOnJoin);
          setWebcamOn(!cameraOffOnJoin);
        } else {
          // Participants join with mic and webcam OFF by default
          setMicOn(false);
          setWebcamOn(false);
        }
        // console.log("meetingRole", meeting.role);
        const allowGuest = localStorage.getItem("allowguest");
        // Generate and store a stable participant ID for this meeting
        // This prevents meeting reinitialization caused by changing participantId
        let participantIdForMeeting = localStorage.getItem(meeting.roomId);
        if (!participantIdForMeeting) {
          // generate a random unique alphanumeric string 12 characters long
          participantIdForMeeting = Math.random().toString(36).substring(2, 15);
          localStorage.setItem(meeting.roomId, participantIdForMeeting);
        }
        // Set the stable participant ID in state (user.id takes priority if available)
        setStableParticipantId(participantIdForMeeting);
        // console.log("allowGuest", isauth, allowGuest);
        // Only redirect to login if meeting explicitly requires authentication
        // (e.g., has requireAuth flag set). For regular meetings, allow anyone to join.
        if (meeting.isNow && !isauth && !allowGuest && meeting.requireAuth) {
          console.log("navigate to login - meeting requires authentication");
          localStorage.setItem("roomId", roomName);
          navigate(`/login`);
        } else if (meeting.isNow && meeting.roomType === "Webinar" && meeting.requireRegistration && !isHostRole && !isRecorderBot) {
          // Webinar requires registration — check if participant has registered.
          // If the URL carries a registration token (?r=), resolve it into a
          // populated localStorage entry so email-link arrivals bypass the
          // registration form even on first visit on this device.
          let storedReg = localStorage.getItem(`webinar_reg_${meeting.roomId}`);
          const urlToken = new URLSearchParams(window.location.search).get('r');
          if (!storedReg && urlToken) {
            try {
              const verifyRes = await meetingServices.resolveRegistrationByToken(meeting.roomId, urlToken);
              if (verifyRes?.success && verifyRes.registrant) {
                const regData = {
                  firstName: verifyRes.registrant.firstName,
                  lastName: verifyRes.registrant.lastName,
                  email: verifyRes.registrant.email,
                  token: urlToken,
                };
                localStorage.setItem(`webinar_reg_${meeting.roomId}`, JSON.stringify(regData));
                storedReg = JSON.stringify(regData);
              }
            } catch (err) {
              console.warn('[Conference] registration token lookup failed:', err?.message);
            }
          }
          if (!storedReg) {
            // Not registered — redirect to registration page (preserve token if present)
            const suffix = urlToken ? `?r=${encodeURIComponent(urlToken)}` : '';
            navigate(`/webinar/${meeting.roomId}/event${suffix}`, { replace: true });
            return;
          }
          // Registered — set participant name from registration data
          try {
            const regData = JSON.parse(storedReg);
            const regName = [regData.firstName, regData.lastName].filter(Boolean).join(" ") || "Guest";
            setParticipantName(regName);
          } catch (_) {
            setParticipantName("Guest");
          }
          const jointokenresp = await meetingServices.getJoiningToken({ roomId: meeting.roomId });
          if (jointokenresp.token) {
            setJoiningToken(jointokenresp.token);
          }
          // Check if waiting room is enabled — registered webinar attendees
          // must still go through it unless autoAdmitRegistered is on (handled server-side)
          const webinarWaitingRoom = meeting.waitingRoom === true || meeting.hostApprovalRequired === true;
          if (webinarWaitingRoom && !isHostRole) {
            // Go through JoiningScreenNew which handles the waiting room flow
            localStorage.removeItem("roomId");
            localStorage.removeItem("allowguest");
          } else {
            // No waiting room — auto-start meeting
            setMeetingStarted(true);
            localStorage.removeItem("roomId");
            localStorage.removeItem("allowguest");
          }
        } else if (meeting.isNow) {
          const jointokenresp = await meetingServices.getJoiningToken({ roomId: meeting.roomId });
          if (jointokenresp.token) {
            setJoiningToken(jointokenresp.token);
          }
          localStorage.removeItem("roomId");
          localStorage.removeItem("allowguest");
        }
        // Set show to true AFTER meetingSettings is loaded to prevent race condition
        setShow(true);
        setIsLoading(false);
      } else {
        navigate(`/meeting-not-found`);
      }
    } catch (error) {
      console.log("Error in checkValidMeeting", error);
      console.log("Error details:", {
        status: error?.response?.status,
        isauth,
        retryAsGuest,
        pathname: window.location.pathname
      });

      // If we got a 401 error and were trying as authenticated user, retry as guest
      if (error?.response?.status === 401 && isauth && !retryAsGuest) {
        console.log("Token expired or invalid, retrying as guest...");
        // Token was already cleared by the interceptor, retry as guest
        setTokenContext(null);
        setIsAuthenticated(false);
        checkValidMeeting(roomName, false, true);
        return;
      }

      // Still show screen on error so user can see an error state
      setShow(true);
      setIsLoading(false);
    }
  }

  useEffect(() => {
    const pathname = window.location.pathname;
    const roomName = pathname.split('/').pop();
    const token = localStorage.getItem('token');
    if (token) {
      const inuser = JSON.parse(localStorage.getItem('user'));
      setTokenContext(token);
      setIsAuthenticated(true);
      setUser((prev) => ({
        ...(prev || {}),
        ...(inuser || {}),
      }));
      console.log("user", inuser);
      // Use saved breakout name if coming from breakout redirect, otherwise use user name
      const breakoutName = isFromBreakout ? localStorage.getItem('rlink_breakout_name') : null;
      // Don't remove rlink_breakout_name here — keep it for when participant leaves the breakout room
      // It will be cleaned up by the breakout leave/return handler
      // For Meetings launched from the setup page (/dashboard/setup?type=meeting),
      // honour the Display Name the user typed there instead of the authenticated
      // user's stored name. The flag is set by MeetingSetup.handleMeetNow only for
      // sessionType === "meeting" and is consumed once.
      const useSetupDisplayName = localStorage.getItem("rlink_useSetupDisplayName") === "true";
      const setupDisplayName = useSetupDisplayName ? localStorage.getItem("displayName") : null;
      if (useSetupDisplayName) {
        localStorage.removeItem("rlink_useSetupDisplayName");
      }
      setParticipantName(breakoutName || setupDisplayName || inuser?.name);
      checkValidMeeting(roomName, true);
    } else {
      // For unauthenticated users coming from breakout redirect, preserve their name
      if (isFromBreakout) {
        const breakoutName = localStorage.getItem('rlink_breakout_name');
        if (breakoutName) {
          setParticipantName(breakoutName);
          // Don't remove — keep for when participant leaves breakout room
        }
      }
      checkValidMeeting(roomName, false);
    }
    // Note: setShow(true) is now called inside checkValidMeeting after meetingSettings is loaded
    // This prevents the race condition where the UI rendered before meetingSettings was available
  }, []);

  // Auto-start meeting when joining from breakout redirect (skip lobby)
  useEffect(() => {
    if (isFromBreakout && joiningToken && show && !isMeetingStarted) {
      console.log('[Breakout] Auto-starting meeting from breakout redirect');
      setMeetingStarted(true);
      // Clean up ?fromBreakout=1 from URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [isFromBreakout, joiningToken, show, isMeetingStarted]);

  // Breakout room leave is now handled in MeetingContainerNew._handleMeetingLeft
  // before the meeting UI unmounts (so participant name and socket are still available)

  useEffect(() => {
    if (isMobile) {
      window.onbeforeunload = () => {
        // Skip the "Leave site?" prompt during breakout room redirects
        if (window.__breakoutRedirecting) return undefined;
        return "Are you sure you want to exit?";
      };
    }
  }, [isMobile]);

  // Memoize metaData to prevent AgoraMeetingProvider from restarting its
  // polling interval on every parent re-render. Without this, a new object
  // reference is created each render, causing the useEffect that depends on
  // metaData to tear down and recreate the 2s polling interval continuously.
  // Generate a unique browser session ID for this tab.
  // Survives page refresh (sessionStorage) but is different across tabs/devices.
  const browserSessionId = useMemo(() => {
    let id = sessionStorage.getItem('rlink_browser_session_id');
    if (!id) {
      id = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem('rlink_browser_session_id', id);
    }
    return id;
  }, []);

  const stableMetaData = useMemo(() => ({
    profileImage: user?.logo,
    meetingRole: initialMeetingRole || meetingRole,
    userId: user?.id,
    browserSessionId,
  }), [user?.logo, initialMeetingRole, meetingRole, user?.id, browserSessionId]);

  // Diagnostic: log when any condition for rendering the meeting UI changes
  // This helps diagnose white-screen issues where the meeting UI disappears
  useEffect(() => {
    const shouldRender = show && meetingSettings?.isNow && (user?.id || stableParticipantId);
    if (!shouldRender && show) {
      console.warn('[Conference] Meeting UI hidden! Conditions:', {
        show,
        isNow: meetingSettings?.isNow,
        userId: user?.id,
        stableParticipantId,
        isMeetingStarted,
        isMeetingLeft,
      });
    }
  }, [show, meetingSettings?.isNow, user?.id, stableParticipantId, isMeetingStarted, isMeetingLeft]);

  const meetingMode = () => {
    // Participant cap: when the meeting has hit 100 participants, new
    // joiners get view-only (RECV_ONLY) so they can watch + listen but
    // not publish video/audio. Hosts/co-hosts are exempt (the backend
    // never sets viewOnly for them). This keeps Agora costs down and
    // matches the plan limit of 100 active participants.
    if (meetingSettings?.viewOnly) {
      return Constants.modes.RECV_ONLY;
    }
    if (meetingSettings && meetingSettings.roomType == "Webinar" && !["moderator", "co-host", "host", "speaker", "presenter"].includes(meetingRole)) {
      return Constants.modes.RECV_ONLY;
    } else {
      return Constants.modes.SEND_AND_RECV;
    }
  }

  // Detect recorder mode (same check MeetingContainerNew uses) so we can hide
  // all cursors globally. Xvfb's `-nocursor` flag only hides the X server's
  // root cursor — Chrome still draws its own (CSS-driven) cursor inside the
  // page, which shows up as a black arrow in the captured video. A global
  // CSS `cursor: none !important` rule covers it for the whole recorder page.
  const isRecorderPage = new URLSearchParams(window.location.search).get('recorder') === 'true';

  return (
    <>
      {isRecorderPage && (
        <style dangerouslySetInnerHTML={{
          __html: '*, *::before, *::after { cursor: none !important; }',
        }} />
      )}
      {/* Loading state while fetching meeting data. Hidden in grid-recorder
          mode because this loader is a single `fixed inset-0` overlay at the
          viewport level — outside the 4 grid cells, so the transform-based
          containing block can't capture it. Without this guard, ffmpeg
          captures the loader at 2560x1440 and cropGridLayouts splits it into
          4 quadrants that each show a different piece of the loader.
          In grid mode we just show black until each cell finishes init. */}
      {isLoading && !forceGrid && (
        <div className="fixed inset-0 bg-black flex items-center justify-center z-[9999]">
          <div className="flex flex-col items-center justify-center gap-6">
            <div className="animate-pulse">
              <RLinkLogo size="xl" companyLogo={wlBranding.companyLogo} companyName={wlBranding.companyName} />
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-purple-500 animate-bounce" style={{ animationDelay: '0s' }}></div>
              <div className="w-2 h-2 rounded-full bg-purple-500 animate-bounce" style={{ animationDelay: '0.1s' }}></div>
              <div className="w-2 h-2 rounded-full bg-purple-500 animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            </div>
            <p className="text-gray-400 text-sm">Loading meeting...</p>
          </div>
        </div>
      )}
      {show && meetingSettings?.isNow && (user?.id || stableParticipantId) && <ErrorBoundary><MeetingAppProvider>
        {isMeetingLeft ? (
          // Suppress the leave screen in grid-recorder mode — it renders as a
          // viewport-level overlay outside the 4 grid cells, so the capture
          // ffmpeg picks it up at 2560x1440 and cropGridLayouts splits it
          // into 4 cropped-quadrant copies on each final MP4. For the recorder
          // bot, just blank the page; the worker will stop the capture shortly.
          forceGrid ? null : <LeaveScreenNew setIsMeetingLeft={setIsMeetingLeft} meetingId={meetingId} />
        ) : isMeetingStarted ? (
          <MeetingProvider
            config={{
              meetingId,
              // Use stable participantId from state to prevent meeting reinitialization
              // user?.id takes priority for authenticated users, otherwise use the stored stable ID
              participantId: user?.id || stableParticipantId,
              micEnabled: micOn,
              webcamEnabled: webcamOn,
              name: participantName ? participantName : "No Name",
              multiStream: true,
              metaData: stableMetaData,
              permissions: {
                removeParticipant: meetingRole && meetingRole == "moderator",
              },
              // Use webinarMode if available, fall back to meetingMode() which checks roomType/role.
              // CRITICAL: recorder mode forces RECV_ONLY so no local mic/cam tracks are ever
              // created — recorder joins as a silent observer, never shows up as a participant
              // tile and cannot be picked as active speaker.
              mode: (new URLSearchParams(window.location.search).get('recorder') === 'true')
                ? Constants.modes.RECV_ONLY
                : (webinarMode || meetingMode()),
              translationLanguage: "en",
              speakingLanguage: "en",
            }}
            token={joiningToken}
            reinitialiseMeetingOnConfigChange={true}
            joinWithoutUserInteraction={true}
          >
            {/* Wrap with MeetingStateProvider for late joiner state sync */}
            <MeetingStateProvider
              meetingId={meetingId}
              participantId={user?.id || stableParticipantId}
              participantName={participantName}
              isHost={["moderator", "co-host", "host"].includes(meetingRole)}
              sessionName={customMeetingName || meetingSettings?.meetingName || meetingSettings?.roomName || ''}
              sessionType={meetingSettings?.roomType || 'meeting'}
            >
              {/* Wrap with AgoraRTMProvider for chat and PubSub functionality.
                  AgoraChatProvider is skipped for recorder bots — chat panel
                  is hidden via isRecorderMode in MeetingContainerNew anyway,
                  so the chat subscription / lifecycle work is pure overhead
                  for the bot Chrome. AgoraRTM stays mounted because state
                  pubsub (active CTAs, polls, branding) flows through it. */}
              <AgoraRTMProvider
                meetingId={meetingId}
                userId={user?.id || stableParticipantId}
                userName={participantName}
              >
                {isRecorderPage ? (
                  <>
                    {meetingSettings && (meetingSettings.roomType == "Meeting" || meetingSettings.roomType == "Live Stream" || meetingSettings.roomType == "Webinar") && (
                      forceGrid ? (
                        // Grid-recorder mode: 4 MeetingContainerNew siblings inside ONE
                        // shared MeetingProvider → one Agora SDK, one WebRTC peer
                        // connection, one decode per stream (not 4). Each cell gets
                        // its own layout via `recorderLayoutOverride`; all other
                        // shared state (participants, active speaker, etc.) reads
                        // from the common context. 2x2 CSS grid at 2560x1440 so the
                        // worker's single FFmpeg x11grab captures the whole canvas,
                        // then cropGridLayouts splits it into 4 per-layout MP4s at
                        // 1280x720 each (no downscale needed — capture IS the
                        // output resolution).
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '1280px 1280px',
                            gridTemplateRows: '720px 720px',
                            width: 2560,
                            height: 1440,
                            background: '#000',
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            margin: 0,
                            padding: 0,
                            gap: 0,
                          }}
                        >
                          {RECORDER_GRID_LAYOUTS.map((layout) => (
                            <div
                              key={layout}
                              style={{
                                width: 1280,
                                height: 720,
                                overflow: 'hidden',
                                position: 'relative',
                                // `transform` creates a new containing block so that
                                // descendant `position: fixed` elements (CTAs, web
                                // overlays, presentations, element overlays in
                                // MeetingContainerNew) position relative to this
                                // 1280x720 cell instead of the 2560x1440 viewport.
                                // Without this, overlays render at viewport coords
                                // and get clipped to whichever cell they intersect.
                                transform: 'translateZ(0)',
                              }}
                            >
                              <MeetingContainerNew
                                recorderLayoutOverride={layout}
                                onMeetingLeave={() => {
                                  setToken("");
                                  setMeetingId("");
                                  setParticipantName("");
                                  setWebcamOn(false);
                                  setMicOn(false);
                                  setMeetingStarted(false);
                                }}
                                setIsMeetingLeft={setIsMeetingLeft}
                                setCustomVideoStream={setCustomVideoStream}
                                micOn={micOn}
                                setMicOn={setMicOn}
                                webcamOn={webcamOn}
                                setWebcamOn={setWebcamOn}
                                customAudioStream={customAudioStream}
                                setCustomAudioStream={setCustomAudioStream}
                                customVideoStream={customVideoStream}
                              />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <MeetingContainerNew
                          onMeetingLeave={() => {
                            setToken("");
                            setMeetingId("");
                            setParticipantName("");
                            setWebcamOn(false);
                            setMicOn(false);
                            setMeetingStarted(false);
                          }}
                          setIsMeetingLeft={setIsMeetingLeft}
                          setCustomVideoStream={setCustomVideoStream}
                          micOn={micOn}
                          setMicOn={setMicOn}
                          webcamOn={webcamOn}
                          setWebcamOn={setWebcamOn}
                          customAudioStream={customAudioStream}
                          setCustomAudioStream={setCustomAudioStream}
                          customVideoStream={customVideoStream}
                        />
                      )
                    )}
                    {/* WebinarContainer disabled — webinars now use MeetingContainerNew for consistent UI and elements support */}
                  </>
                ) : (
                  <AgoraChatProvider
                    meetingId={meetingId}
                    userId={user?.id || stableParticipantId}
                    userName={participantName}
                  >
                    {meetingSettings && (meetingSettings.roomType == "Meeting" || meetingSettings.roomType == "Live Stream" || meetingSettings.roomType == "Webinar") && (
                      <MeetingContainerNew
                        onMeetingLeave={() => {
                          setToken("");
                          setMeetingId("");
                          setParticipantName("");
                          setWebcamOn(false);
                          setMicOn(false);
                          setMeetingStarted(false);
                        }}
                        setIsMeetingLeft={setIsMeetingLeft}
                        setCustomVideoStream={setCustomVideoStream}
                        micOn={micOn}
                        setMicOn={setMicOn}
                        webcamOn={webcamOn}
                        setWebcamOn={setWebcamOn}
                        customAudioStream={customAudioStream}
                        setCustomAudioStream={setCustomAudioStream}
                        customVideoStream={customVideoStream}
                      />
                    )}
                  </AgoraChatProvider>
                )}
              </AgoraRTMProvider>
            </MeetingStateProvider>
          </MeetingProvider>
        ) : (
          <JoiningScreenNew
            participantName={participantName}
            setParticipantName={setParticipantName}
            setMeetingId={setMeetingId}
            meetingId={meetingId}
            setToken={setToken}
            micOn={micOn}
            setMicOn={setMicOn}
            webcamOn={webcamOn}
            setWebcamOn={setWebcamOn}
            customAudioStream={customAudioStream}
            setCustomAudioStream={setCustomAudioStream}
            customVideoStream={customVideoStream}
            setCustomVideoStream={setCustomVideoStream}
            onClickStartMeeting={() => {
              unlockIOSAudio();
              setMeetingStarted(true);
            }}
            startMeeting={isMeetingStarted}
            setIsMeetingLeft={setIsMeetingLeft}
            joiningToken={joiningToken}
            setJoiningToken={setJoiningToken}
          />
        )}
      </MeetingAppProvider></ErrorBoundary>}
      {meetingSettings && !meetingSettings?.isNow && show &&
        (<>
          {meetingSettings?.roomType === "Webinar" ? (
            <RedirectToRegistration roomId={meetingSettings?.roomId} />
          ) : (
            <>
              {!meetingSettings?.isPast && <WebinerBrandingPre />}
              {meetingSettings?.isPast && <WebinerBrandingPost />}
            </>
          )}
        </>
        )
      }
      {isMeetingReconnecting && !forceGrid && (
        <div className="fixed inset-0 bg-[#00000014] backdrop-blur-sm z-[9999] flex items-center justify-center animate-fadeIn">
          <div className="flex flex-col items-center justify-center gap-6 p-8 rounded-2xl backdrop-blur-md shadow-2xl border animate-scaleIn" style={{ backgroundColor: '#7700ff7a', borderColor: 'rgba(98, 70, 251, 0.5)' }}>
            {/* Animated Spinner */}
            <div className="relative w-20 h-20">
              <div className="absolute inset-0 border-4 border-gray-700 rounded-full"></div>
              <div className="absolute inset-0 border-4 border-transparent rounded-full animate-spin" style={{ borderTopColor: '#6246FB' }}></div>
              <div className="absolute inset-0 border-4 border-transparent rounded-full animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s', borderRightColor: '#586FEA' }}></div>
              <div className="absolute inset-2 border-4 border-transparent rounded-full animate-spin" style={{ animationDuration: '2s', borderBottomColor: '#4F63D2' }}></div>
            </div>

            {/* Pulsing Dots */}
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full animate-pulse" style={{ animationDelay: '0s', backgroundColor: '#6246FB' }}></div>
              <div className="w-3 h-3 rounded-full animate-pulse" style={{ animationDelay: '0.2s', backgroundColor: '#586FEA' }}></div>
              <div className="w-3 h-3 rounded-full animate-pulse" style={{ animationDelay: '0.4s', backgroundColor: '#4F63D2' }}></div>
            </div>

            {/* Text */}
            <div className="flex flex-col items-center gap-2">
              <h2 className="text-white text-2xl md:text-3xl font-bold tracking-wide animate-pulse">
                Reconnecting
              </h2>
              <p className="text-gray-300 text-sm md:text-base font-medium">
                Please wait while we restore your connection...
              </p>
            </div>

            {/* Progress Bar Animation */}
            <div className="w-64 h-1 bg-gray-700 rounded-full overflow-hidden">
              <div className="h-full rounded-full animate-progress" style={{ background: 'linear-gradient(to right, #6246FB, #586FEA, #4F63D2)' }}></div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Conference;
